import React, { useState, useEffect, useRef } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import SenseCount from '../../annotations/SenseCount';
import { describeSense, distinguishingLabel } from '../../yasna/senseLabel';
import { createAnnotation } from '../../annotations/annotationStore';
// Shares the subtitle form's look (.subtitle-form* classes) and the sense
// picker's rows (.sense-picker-*).
import './SubtitlePanel.css';
import '../../yasna/SensePicker.css';
import './MomentAnnotator.css';

/**
 * WordAnnotateForm — annotate a word or phrase heard in the current time
 * window (the Add panel's window, shared with the subtitle form). The
 * annotation is anchored to that window (subtitleId: null), so it belongs to
 * whatever subtitle shows then.
 *
 * One click on Annotate searches yasna for the text:
 *   - exactly one sense → the annotation is saved straight away
 *   - several senses   → the teacher taps the one used here, which saves it
 *   - no sense         → a link to add the word in yasna
 *
 * Props:
 *   videoId, language ('fr' | 'de')
 *   yasna, yasnaError — from useYasna()
 *   start, end — the window, in seconds (end null: until the next subtitle)
 *   anchor — optional subtitleId to save with: TITLE_ANCHOR for a word of
 *            the video's title (start and end then null). Default null:
 *            anchored to the time window.
 *   label, inputId, placeholder — optional field text (several forms on a page)
 *   request — optional { text, n }: annotate this word now, as if typed and
 *             Annotate clicked (a gap in the word-for-word line, Requirement
 *             63). A new n asks again.
 *   onSaved(annotation)
 */
function WordAnnotateForm({
  videoId, language, yasna, yasnaError, start, end, onSaved,
  anchor = null, label = 'Annotate a word or phrase', inputId = 'moment-text', placeholder = 'e.g. se rendre compte',
  request = null,
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // What the last Annotate click came to:
  //   { kind: 'choose', term, senses, times } | { kind: 'missing', term }
  //   | { kind: 'saved', annotation, sense } | { kind: 'error', message }
  const [outcome, setOutcome] = useState(null);

  // Another video starts afresh.
  useEffect(() => {
    setText('');
    setOutcome(null);
  }, [videoId]);

  const canAnnotate = !!yasna && !busy && text.trim() && (end == null || end >= start);

  // times: the window when Annotate was clicked, kept while a sense is picked.
  const saveAnnotation = async (sense, surfaceText, times) => {
    setBusy(true);
    try {
      const annotation = await createAnnotation({
        videoId,
        subtitleId: anchor, // null: anchored to its time window; TITLE_ANCHOR: the title
        startTime: times.start,
        endTime: times.end,
        surfaceText,
        senseId: sense.word_sense_id,
        language: sense.language,
        wordIndices: [],
        annotatorNote: null,
        createdAt: serverTimestamp(),
        createdBy: 'annotator',
      });
      setOutcome({ kind: 'saved', annotation, sense });
      setText(''); // ready for the next word in the same window
      onSaved && onSaved(annotation);
    } catch (e) {
      console.error('Error saving annotation:', e);
      setOutcome({ kind: 'error', message: 'Failed to save the annotation. Please try again.' });
    }
    setBusy(false);
  };

  const handleAnnotate = async (e) => {
    e.preventDefault();
    if (!canAnnotate) return;
    await annotate(text.trim());
  };

  // A word asked for from outside (request): annotate it once yasna is
  // ready, once per request.
  const handledRef = useRef(null);
  useEffect(() => {
    if (!request || !request.text || !yasna || handledRef.current === request.n) return;
    handledRef.current = request.n;
    setText(request.text);
    annotate(request.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request && request.n, yasna]);

  const annotate = async (term) => {
    if (!yasna || !term || (end != null && end < start)) return;
    const times = { start, end }; // the video may move on while a sense is picked
    setOutcome(null);
    setBusy(true);
    let senses;
    try {
      senses = await yasna.search(term, language);
    } catch (err) {
      setOutcome({ kind: 'error', message: "Couldn't reach the dictionary. Please try again." });
      setBusy(false);
      return;
    }
    setBusy(false);
    if (senses.length === 0) setOutcome({ kind: 'missing', term });
    else if (senses.length === 1) await saveAnnotation(senses[0], term, times);
    else setOutcome({ kind: 'choose', term, senses, times });
  };

  return (
    <form className="word-annotate-form" onSubmit={handleAnnotate}>
      <div className="subtitle-form-field">
        <label className="subtitle-form-label" htmlFor={inputId}>{label}</label>
        <div className="word-annotate-row">
          <input
            id={inputId}
            className="subtitle-form-input"
            type="text"
            placeholder={placeholder}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              // A different word: drop the choices/link for the old one.
              if (outcome && outcome.kind !== 'saved') setOutcome(null);
            }}
            autoCapitalize="off"
            spellCheck="false"
          />
          <button type="submit" className="subtitle-form-save-btn word-annotate-btn" disabled={!canAnnotate}>
            {busy ? 'Annotating…' : 'Annotate'}
          </button>
        </div>
      </div>

      {yasnaError && <p className="subtitle-form-error">{yasnaError}</p>}
      {!yasna && !yasnaError && <p className="moment-annotator-status">Loading dictionary…</p>}

      {outcome && outcome.kind === 'saved' && (
        <p className="moment-annotator-saved" role="status">
          Annotated "{outcome.annotation.surfaceText}" → {describeSense(outcome.sense)}
        </p>
      )}

      {outcome && outcome.kind === 'error' && (
        <p className="subtitle-form-error">{outcome.message}</p>
      )}

      {outcome && outcome.kind === 'missing' && (
        <div className="sense-picker-missing">
          <p>"{outcome.term}" isn't in the dictionary yet.</p>
          <a href={yasna.teacherUrl(outcome.term, language)} target="_blank" rel="noreferrer">
            Add it in Yasna ↗
          </a>
          <p className="sense-picker-hint">Once it's saved there, click Annotate again.</p>
        </div>
      )}

      {outcome && outcome.kind === 'choose' && (
        <div className="moment-annotator-choose">
          <p className="moment-annotator-choose-label">
            "{outcome.term}" has more than one meaning. Pick the one used here:
          </p>
          <ul className="sense-picker-results" aria-label="Dictionary senses">
            {outcome.senses.map((s) => (
              <li key={s.word_sense_id}>
                <button
                  type="button"
                  className="sense-picker-row"
                  onClick={() => saveAnnotation(s, outcome.term, outcome.times)}
                  disabled={busy}
                >
                  <span className="sense-picker-word">{s.word}</span>
                  {distinguishingLabel(s, outcome.senses) && <span className="sense-picker-label">{distinguishingLabel(s, outcome.senses)}</span>}
                  {s.short_gloss && <span className="sense-picker-gloss">{s.short_gloss}</span>}
                  <SenseCount senseId={s.word_sense_id} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
}

export default WordAnnotateForm;
